import React, { useMemo } from 'react'
import moment from 'moment'
import DashboardEditableRow from '@components/pages/Dashboard/DashboardEditableRow.component'
import { ProviderUtilizationCard } from '@components/pages/Dashboard/Performance/SectionCards'
import {
  buildDashboardDetailHref,
  DASHBOARD_DETAIL_CONTEXT,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import { DashboardSection } from '@constants/Dashboard/dashboard.enums'
import { OVERVIEW_DEFAULT_HIDDEN_SECTIONS } from '@constants/Dashboard/huddleSectionVisibility.constants'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  AppointmentOverrunCard,
  AppointmentsCard,
  ARAgingOverviewCard,
  CalendarCard,
  CaseAcceptanceCard,
  ChairTimeCard,
  IncompleteTreatmentPlanCard,
  NoShowRateCard,
  OpenTreatmentPatientsCard,
  OverviewAlertsCard,
  OverviewCollectionCard,
  OverviewDiagnosisSectionContent,
  OverviewRecareSectionContent,
  OverviewTreatmentAcceptanceSectionContent,
  OverviewUtilizationMetrics,
  OwnerKpiMetrics,
  PatientFlowTrackerCard,
  ProceduresCard,
  ProductionOverviewCard,
  RecallCard,
  SameDayCancellationCard,
  TasksCard,
} from './SectionCards'
import { useOverviewDashboard } from './useOverviewDashboard'
import '@components/pages/Appointments/Scheduling.style.css'
import '@components/pages/Dashboard/DashboardLayout.style.css'
import '@components/pages/Dashboard/Operations/DashboardOperations.style.css'

function isOverviewSectionVisible(
  name: DashboardSection,
  hiddenSections: Set<string>,
): boolean {
  return (
    !OVERVIEW_DEFAULT_HIDDEN_SECTIONS.has(name) && !hiddenSections.has(name)
  )
}

type DashboardOverviewProps = {
  date: string
  onDateChange: (dateString: string) => void
  onPrevDay: () => void
  onNextDay: () => void
  editMode?: boolean
  hiddenSections?: Set<string>
  range?: RangeKey
}

function DashboardOverview({
  date,
  onDateChange,
  editMode = false,
  hiddenSections = new Set(),
  range = '1D',
}: DashboardOverviewProps): React.ReactElement {
  const show = (section: DashboardSection): boolean =>
    isOverviewSectionVisible(section, hiddenSections)

  const overview = useOverviewDashboard({
    range,
    date,
    hiddenSections,
  })
  const utilization = overview.utilization

  const caseAcceptanceHref = useMemo(
    () =>
      buildDashboardDetailHref(DASHBOARD_DETAIL_HREFS.caseAcceptance, {
        context: DASHBOARD_DETAIL_CONTEXT.overview,
        range,
        date: moment(date, 'MMMM D, YYYY').format('YYYY-MM-DD'),
      }),
    [range, date],
  )
  const treatmentAcceptanceHref = useMemo(
    () =>
      buildDashboardDetailHref(DASHBOARD_DETAIL_HREFS.treatmentAcceptance, {
        context: DASHBOARD_DETAIL_CONTEXT.overview,
        range,
        date: moment(date, 'MMMM D, YYYY').format('YYYY-MM-DD'),
      }),
    [range, date],
  )
  const diagnosisHref = useMemo(
    () =>
      buildDashboardDetailHref(DASHBOARD_DETAIL_HREFS.diagnosis, {
        context: DASHBOARD_DETAIL_CONTEXT.overview,
        range,
        date: moment(date, 'MMMM D, YYYY').format('YYYY-MM-DD'),
      }),
    [range, date],
  )

  const showAppointmentsCombinedSection =
    show(DashboardSection.Appointments) || show(DashboardSection.TodaysSchedule)
  const utilizationLoading =
    utilization.schedulingLoading ||
    utilization.clinicalEfficiencyLoading ||
    utilization.diagnosedTreatmentLoading ||
    utilization.caseAcceptanceLoading

  return (
    <div className="dashboard-overview">
      <DashboardEditableRow
        editMode={editMode}
        className="dashboard-overview-schedule-calendar-row"
        lastCellFlexBasis={
          showAppointmentsCombinedSection && show(DashboardSection.Calendar)
            ? '400px'
            : undefined
        }
      >
        {showAppointmentsCombinedSection && (
          <AppointmentsCard
            date={date}
            range={range}
            showAppointments={show(DashboardSection.Appointments)}
            showTodaySchedule={show(DashboardSection.TodaysSchedule)}
            schedulingOverview={utilization.schedulingOverview}
            schedulingOverviewDataParams={
              utilization.schedulingOverviewDataParams
            }
            schedulingLoading={utilization.schedulingLoading}
            appointmentSecondaryMetrics={
              utilization.appointmentSecondaryMetrics
            }
            operatoriesSettled={utilization.operatoriesSettled}
            todayScheduleAppointments={overview.todayScheduleApi.data}
            appointmentFetchLoading={overview.todayScheduleApi.loading}
          />
        )}
        {show(DashboardSection.Calendar) && (
          <CalendarCard date={date} onDateChange={onDateChange} />
        )}
      </DashboardEditableRow>

      <OwnerKpiMetrics
        editMode={editMode}
        metrics={utilization.ownerOverviewKpiMetrics}
        loading={
          utilization.clinicalEfficiencyLoading ||
          utilization.caseAcceptanceLoading
        }
        hasPayload={utilization.ownerKpiHasPayload}
        hiddenSections={hiddenSections}
      />

      {show(DashboardSection.ProductionTodays) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-production-recare-row"
        >
          <ProductionOverviewCard
            title="Production (Todays)"
            titleHref={overview.hrefs.production}
            className="dashboard-todays-production-card dashboard-acceptance-card"
            production={overview.productionApi}
          />
        </DashboardEditableRow>
      )}

      {show(DashboardSection.PatientFlowTracker) && (
        <PatientFlowTrackerCard
          patientFlowOverview={overview.patientFlowApi.data}
          loading={overview.patientFlowApi.loading}
        />
      )}

      {(show(DashboardSection.IncompleteTreatmentPlan) ||
        show(DashboardSection.OpenTreatmentPatients)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-overview-tables-row"
        >
          {show(DashboardSection.IncompleteTreatmentPlan) && (
            <IncompleteTreatmentPlanCard
              range={range}
              data={overview.incompleteTreatmentApi.data}
              loading={overview.incompleteTreatmentApi.loading}
            />
          )}
          {show(DashboardSection.OpenTreatmentPatients) && (
            <OpenTreatmentPatientsCard
              range={range}
              data={overview.openTreatmentApi.data}
              loading={overview.openTreatmentApi.loading}
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.UtilizationMetrics) && (
        <OverviewUtilizationMetrics
          editMode={editMode}
          loading={utilizationLoading}
          hasPayload={utilization.utilizationMetricsHasPayload}
          row1={utilization.utilizationRow1StandaloneMetrics}
          row2={utilization.displayUtilizationRow2Metrics}
          row3={utilization.displayUtilizationRow3Metrics}
        />
      )}

      {(show(DashboardSection.Procedures) ||
        show(DashboardSection.ProviderUtilization)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-overview-procedures-row"
        >
          {show(DashboardSection.Procedures) && (
            <ProceduresCard
              titleHref={overview.hrefs.procedures}
              procedures={overview.proceduresApi}
            />
          )}
          {show(DashboardSection.ProviderUtilization) && (
            <ProviderUtilizationCard
              rows={utilization.providerUtilizationRows}
              loading={utilization.providerUtilizationLoading}
              hasPayload={utilization.providerUtilizationHasPayload}
              titleHref="/dashboard/provider-utilization?context=overview"
              maxRows={null}
              disableBodyScroll
              tableClassName="dashboard-overview-provider-util-table-wrap"
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.ArAging) ||
        show(DashboardSection.Alerts) ||
        show(DashboardSection.Tasks)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-overview-ar-alerts-row"
        >
          {show(DashboardSection.ArAging) && (
            <ARAgingOverviewCard
              titleHref={overview.hrefs.arAging}
              arAging={overview.arAgingApi}
            />
          )}
          {show(DashboardSection.Alerts) && (
            <OverviewAlertsCard alerts={overview.alertsApi} />
          )}
          {show(DashboardSection.Tasks) && <TasksCard range={range} />}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.Recall) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-overview-recall-row"
        >
          <RecallCard
            range={range}
            recallOverview={overview.recallApi.data}
            loading={overview.recallApi.loading}
          />
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.TodaysProgress) ||
        show(DashboardSection.Production) ||
        show(DashboardSection.Collection) ||
        show(DashboardSection.Recare) ||
        show(DashboardSection.Diagnosis)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-production-recare-row"
        >
          {(show(DashboardSection.TodaysProgress) ||
            show(DashboardSection.Production)) && (
            <ProductionOverviewCard
              titleHref={overview.hrefs.production}
              className="dashboard-todays-production-card dashboard-acceptance-card"
              production={overview.productionApi}
            />
          )}
          {show(DashboardSection.Collection) && (
            <OverviewCollectionCard
              titleHref={overview.hrefs.collection}
              collection={overview.collectionsApi}
            />
          )}
          {show(DashboardSection.Recare) && (
            <OverviewRecareSectionContent
              range={range}
              titleHref="/dashboard/recare"
              recareOverview={overview.recareApi.data}
              loading={overview.recareApi.loading}
            />
          )}
          {show(DashboardSection.Diagnosis) && (
            <OverviewDiagnosisSectionContent
              range={range}
              titleHref={diagnosisHref}
              data={overview.diagnosisApi.data}
              loading={overview.diagnosisApi.loading}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.TreatmentAcceptance) ||
        show(DashboardSection.ChairTime) ||
        show(DashboardSection.CaseAcceptance)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-acceptance-row"
        >
          {show(DashboardSection.TreatmentAcceptance) && (
            <OverviewTreatmentAcceptanceSectionContent
              range={range}
              title="Treatment acceptance"
              titleHref={treatmentAcceptanceHref}
              className="dashboard-acceptance-card"
              data={overview.treatmentAcceptanceApi.data}
              loading={overview.treatmentAcceptanceApi.loading}
            />
          )}
          {show(DashboardSection.ChairTime) && (
            <ChairTimeCard
              range={range}
              chairTimeOverview={overview.chairTimeApi.data}
              loading={overview.chairTimeApi.loading}
            />
          )}
          {show(DashboardSection.CaseAcceptance) && (
            <CaseAcceptanceCard
              range={range}
              titleHref={caseAcceptanceHref}
              caseAcceptanceOverview={utilization.caseAcceptanceOverview}
              loading={utilization.caseAcceptanceLoading}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.NoShowRate) ||
        show(DashboardSection.SameDayCancellation)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-overview-line-charts-row"
        >
          {show(DashboardSection.NoShowRate) && (
            <NoShowRateCard
              range={range}
              noShowRateOverview={overview.noShowRateApi.data}
              loading={overview.noShowRateApi.loading}
            />
          )}
          {show(DashboardSection.SameDayCancellation) && (
            <SameDayCancellationCard
              range={range}
              schedulingOverview={utilization.schedulingOverview}
              schedulingLoading={utilization.schedulingLoading}
              sameDayCancellationDetail={overview.sameDayCancellationApi.data}
              loading={overview.sameDayCancellationApi.loading}
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.AppointmentOverrun) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-overview-appointment-overrun-row"
        >
          <AppointmentOverrunCard
            range={range}
            schedulingOverview={utilization.schedulingOverview}
            schedulingLoading={utilization.schedulingLoading}
          />
        </DashboardEditableRow>
      )}
    </div>
  )
}

export default DashboardOverview
